/**
 * One list, one row per PERSON, and AT MOST TWO KNOCKS BEHIND IT
 * (`docs/conversation-model.md` §6.3).
 *
 * The messenger shell has a single conversation list down its left edge. The
 * server answers it with THREADS (`GET /threads`), grouped on the other
 * person's immutable X id and carrying one row per DIRECTION: `incoming` is the
 * knock at the reader's own door, `outgoing` is the knock they made at the
 * other person's. There is exactly one of each, for as long as both people
 * exist — a knock is a conversation, it is never replaced, and nothing about it
 * ever ends by itself. The chapters are deleted with the follow-up mechanism
 * that produced them.
 *
 * WHAT IS DERIVED AND WHAT IS READ. Everything here is a function of payloads
 * that already exist. The one thing the server does not answer is UNREAD, which
 * is not a server fact in this product at all: nothing is delivered, so nothing
 * is "received". It is a local reading mark, and it lives in `lib/seen.ts`
 * beside the browser that made it.
 *
 * STAGE IS THE GATE, AND IT NEVER REGRESSES (§6.3). Both sides of a door read
 * the same field now — `gate`, which only the recipient moves — so the whole
 * translation is one table and it says the same thing in both directions:
 *
 *   own door                              → kind "own-nob", stage "at-door"
 *   at_door, no assessment                → "at-door", in flight (the hearing badge)
 *   at_door, assessed                     → "at-door"
 *   declined                              → "turned-away"
 *   let_in                                → "through" (or "connected", per the pair)
 *   blocked                               → "turned-away" on the sender's side;
 *                                            the recipient is never sent the row
 *   guard closed                          → "turned-away", with the guard's own line
 *
 * THE SENDER IS TOLD, AND THAT IS NEW. `toSenderView` used to carry a status
 * and no decision, so an outgoing row could never read "through": the
 * recipient's reply arriving was the only thing that said so. It carries the
 * GATE now (§6.1), and a decision outranks a band on both sides (§3) — a
 * `let_in` row reads "Let through" to the person who was let in, said by the
 * only party entitled to say it, because the field IS their decision.
 */
import type { HeldSpot, InboxBand, MyKnock, MyWaiting, RecipientKnock, Thread } from "./api";

export type ConversationKind = "own-nob" | "knock";
export type Direction = "incoming" | "outgoing";

/**
 * `pre-knock` is the one stage that is NOT a knock (integration guide §4.1).
 *
 * A held spot — an `interest_registrations` row, somebody who registered
 * interest before the door opened — renders as `kind: "knock"` because it sits
 * in the same list, and it is not a knock: no `knocks` row, no score, no
 * standing, no ranking window, nothing metered (Waiting Room H9). The stage is
 * what keeps the two apart everywhere the list reasons about a row.
 *
 * IT HAS TWO DIRECTIONS, and they are the same fact from two ends of a door
 * that does not exist yet: `incoming` is somebody holding a spot at YOUR handle
 * (`fromHeldSpot`), `outgoing` is a spot YOU hold at somebody else's
 * (`fromMyWaiting`). Neither is a knock and neither is Active; what differs is
 * whose Nob is missing, which is why the chip and the desk read the direction.
 *
 * `composing` is the SECOND stage that is not a knock, and it is not a held spot
 * either — the two must never be conflated. A held spot is a row the SERVER
 * has and this browser is reading; a composing row is a knock this browser is
 * writing and the server has never heard of. Nothing about it is persisted
 * anywhere until the send, which is what makes it the one row in the list whose
 * whole existence is client state (`shell/state.tsx`).
 */
export type Stage = "at-door" | "through" | "turned-away" | "pre-knock" | "composing";

/**
 * How the door is holding this one — the doorman's face, not the reader's.
 *
 * A COARSENING OF THE BAND, ON PURPOSE. The recipient is entitled to the band
 * and the percentile and reads both on the knock itself; what a 32-pixel face
 * in a list can carry is three states, and three is what it gets. Nothing here
 * ever reaches a sender: this is derived on the recipient's own client from a
 * payload only the recipient is served (`toRecipientView`).
 */
export type Standing = "good-fit" | "held" | "needs-attention";

export function standingOf(band: InboxBand): Standing {
  if (band === "TOP_5" || band === "TOP_25" || band === "TIER_STRONG") return "good-fit";
  if (band === "MIDDLE" || band === "TIER_REASONABLE") return "held";
  return "needs-attention";
}

/**
 * ONE SIDE OF A THREAD — the reader's own knock, or the one at their door
 * (§6.3). Everything the list reads off a knock, derived once here from the
 * payload that knock's own surface already answers with, so the row and the
 * pane cannot disagree about it.
 *
 * A thread holds at most two of these and never a list: the follow-up
 * mechanism that made chapters is deleted, and with it the ordering, the
 * divider and the per-chapter loops.
 */
export type Side = {
  knockId: string;
  direction: Direction;
  stage: Stage;
  /** Milliseconds since the epoch — when this conversation opened. */
  openedAt: number;
  /** The latest movement on it — what the row's timestamp and unread read. */
  at: number;
  standing: Standing;
  saved: boolean;
  demoted: boolean;
  archived: boolean;
  /**
   * The doorman has published a read (§6.1, §6.2). Not "the hearing is over":
   * nothing here is over, and the sender's composer is still open under the
   * standing. It is what the Passed filter and the desk's rows are keyed on.
   */
  assessed: boolean;
  /**
   * THE HEARING IS STILL RUNNING — the badge of §6.3's first row: at the door,
   * no read published, and the guard has not stopped it. Both directions can
   * say so, because both sides read the same two facts now.
   */
  inFlight: boolean;
  /**
   * The guard stopped the doorman listening (§8). A safety stop and not a
   * judgement: the gate did not move, the recipient may still decide, and the
   * sender has no composer.
   */
  guardClosed: boolean;
  /**
   * Nob did NOT recommend this knock (ruling R-0925-1 (2)) — an incoming side
   * only, and only once there is a call to have made. The list row says so
   * rather than wearing the same "On you" as a knock Nob recommended.
   */
  notRecommended?: boolean;
  /** The one line under the name, were this the side the row wears. */
  preview: string;
};

/**
 * A row in the list, and the only shape the sidebar knows.
 *
 * `id` is the SERVER'S thread id for a knock thread — opaque here — and one of
 * the reserved ids (`OWN_ID`, `COMPOSE_ID`, `held:`, `spot:`) for the rows
 * that are not threads. The direction, stage, standing and timestamp of a knock
 * thread are the side it WEARS; `incoming` and `outgoing` carry both.
 */
export type Conversation = {
  id: string;
  kind: ConversationKind;
  direction?: Direction;
  stage: Stage;
  /** What to call them. Never blank — falls back to the handle, then to a dash. */
  name: string;
  /** Bare, no `@`. Empty for an email-only sender, who has no handle to show. */
  handle: string;
  /** The one line under the name. The doorman's last word, or the ask. */
  preview: string;
  /** Milliseconds since the epoch. `0` when the server gave no timestamp. */
  at: number;
  unread: number;
  /**
   * Only meaningful for an incoming knock. A sent knock's payload carries no
   * band at all, and the owner's own thread is not being judged by anybody.
   */
  standing: Standing;
  /**
   * The owner kept this one back by hand (FR-6.4 Save).
   *
   * It rides beside `standing` rather than inside it because the two say
   * different things and the list needs both: the standing is what the doorman
   * found, and this is the owner overruling it. Folding a Save into
   * `good-fit` would erase the override at exactly the moment the product
   * promised to show it — a saved knock that missed the bar must LOOK like a
   * saved knock that missed the bar. False for every row that is not an
   * incoming knock.
   */
  saved: boolean;
  /**
   * The owner said this one should not have been surfaced (owner, 2026-08-26).
   *
   * The exact counterweight to `saved`, and it rides beside `standing` for the
   * same reason: a Save is the owner overruling the bar upwards and this is the
   * owner correcting it downwards, and neither is a thing the doorman found.
   * Folding it into `standing` would work for the list and lose the fact —
   * `needs-attention` on a demoted knock has to be readable as "you sent this
   * one back", not as "the doorman was unimpressed". False for every row that
   * is not an incoming knock.
   */
  demoted: boolean;
  /**
   * The sender's cached picture, an own-origin path or null. Only an incoming
   * knock can carry one; where the list's rule says the doorman's face, this is
   * simply not read. It is for the surfaces where the PERSON is standing.
   */
  avatar: string | null;
  /**
   * Put away by the reader, on THEIR side of the door (ruling R-0828-1).
   *
   * A third mark beside `saved` and `demoted`, and the odd one out: those two
   * are things the owner said about the SENDER, and this is a thing they said
   * about their own list. It is per side — an archived incoming knock and an
   * archived outgoing one are two different rows on the server, and archiving
   * either tells the other person nothing.
   *
   * A THREAD IS ARCHIVED WHEN BOTH SIDES ARE (§6.3): the menu archives them
   * together, and a knock arriving in the other direction under a put-away
   * thread is exactly the news that should bring it back into the lists.
   *
   * It is carried on the row rather than filtered on the server for the same
   * reason `saved` is: this list is sliced locally, and Archived is one of the
   * slices. A conversation the reader DELETED has no field here at all — it
   * never arrives.
   */
  archived: boolean;
  /** The doorman has published a read on the side this row wears (§6.1). */
  assessed: boolean;
  /** No read yet, at the door, and the guard has not stopped it (§6.3). */
  inFlight: boolean;
  /** The guard stopped the doorman listening on the side this row wears (§8). */
  guardClosed: boolean;
  /** Nob did not recommend the knock this row wears (R-0925-1 (2)). Incoming only. */
  notRecommended?: boolean;
  /** The door is open both ways (§9). The server's fact about the pair. */
  connected: boolean;
  /** The knock at the reader's own door. `null` when there is none. */
  incoming: Side | null;
  /** The knock the reader made at the other person's door. `null` when there is none. */
  outgoing: Side | null;
  /**
   * A HELD SPOT'S OWN LINE, AND WHETHER IT WAS LET GO OF (F-27, browser walk
   * 2026-09-13).
   *
   * Present on the two `pre-knock` rows and on nothing else — a knock's ask
   * lives in its transcript. It exists because `preview` on a stopped spot is
   * the STOPPED FACT rather than the line: right for a list row, which has one
   * line to say what the row is, and wrong under a heading that says "Your
   * line", where it printed the system's sentence as the person's words.
   */
  spot?: { line: string; stopped: boolean };
  /**
   * THE SERVER'S THREAD ID, ON A ROW THAT WEARS ANOTHER ONE (wave 82). The
   * knock a console has just sent stays under the compose thread's id for as
   * long as that thread is open (`use-conversations.ts`), and its reading mark
   * is kept under the server's id — which the sidebar marks through this, or
   * the knocker's own row kept its unread badge while they were reading it
   * (`md-highprofile`, first UI review). Absent on every other row.
   */
  threadId?: string;
};

/**
 * `archived` is a filter and not a chip, and the distinction is the design.
 *
 * The five chips are a partition of live work, each with a count, and they are
 * sized to fit one row. Archived is not another slice of that work — it is where
 * work goes when the reader is done looking at it, and it is reached from the
 * same control that sorts the list. Putting it in the row would give a drawer
 * the same weight as a triage lane and push the five that matter into a scroll.
 */
export type FilterId = "active" | "passed" | "outgoing" | "incoming" | "all" | "archived";
export type SortId = "recent" | "oldest" | "unread";

/**
 * The chips, in the order they are drawn, `Active` first because it is the
 * default (PRD v4.0 FR-6.2, ruling C8). They filter THREADS by the side the row
 * wears (§6.3); the counts follow.
 *
 * The labels are short — Active · For you · Out · In · All — so every tab is
 * visible on a phone without scrolling; "For you" is what the doorman set
 * aside for the owner plus what they kept, and claims nothing about who
 * decided (owner, 2026-09-11).
 *
 * `Active` is the only filter cut ACROSS direction rather than along it:
 * everything you sent, plus everyone you let through. That is the triage
 * promise — a default that shows everything is a pile, and a pile is the inbox
 * this product said it would abolish. `All` survives underneath as the archive,
 * which is a different thing from a default.
 */
export const FILTERS: { id: FilterId; label: string; hint: string }[] = [
  { id: "active", label: "Active", hint: "Everything you sent, plus everyone you let through" },
  { id: "passed", label: "For you", hint: "Through the filter and waiting on you" },
  { id: "outgoing", label: "Out", hint: "Doors you knocked on" },
  { id: "incoming", label: "In", hint: "Everything that arrived, answered or not" },
  { id: "all", label: "All", hint: "Every conversation, both directions" },
];

/** What the console opens on. One name, so the shell and its tests agree. */
export const DEFAULT_FILTER: FilterId = "active";

/**
 * The sixth list, and the one that is not a chip (ruling R-0828-1).
 *
 * It lives in the sort menu because that is the control the list already has for
 * "how am I looking at this", and because Archived answers a question the chips
 * do not: not "which of these needs me" but "where did I put that one". It keeps
 * its count for the same reason the chips carry theirs — a drawer that does not
 * say how full it is invites nobody to open it.
 */
export const ARCHIVED_FILTER: { id: FilterId; label: string; hint: string } = {
  id: "archived",
  label: "Archived",
  hint: "Conversations you put away, both directions",
};

export const SORTS: { id: SortId; label: string }[] = [
  { id: "recent", label: "Newest" },
  { id: "oldest", label: "Oldest" },
  { id: "unread", label: "Unread first" },
];

/** `null`/unparseable is `0`, which sorts last — not `Date.now()`, which lies. */
export function atOf(iso: string | null): number {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

/** The third namespace. Deliberately not a thread id — see `fromHeldSpot`. */
export const HELD_PREFIX = "held:";

/**
 * The FIFTH namespace: a spot the reader holds at somebody else's handle.
 *
 * It is keyed by the HANDLE rather than by a registration id, and that is not a
 * shortcut — `GET /waiting/mine` answers with no id at all, because a
 * registration is one per person per handle (FR-G3) and the handle is therefore
 * its whole identity. It also makes the id computable from the one thing every
 * entry point into this thread already has: somebody who has just reserved a
 * spot at `@maya` can be dropped into `spot:maya` without waiting to be told
 * what row the server wrote.
 *
 * Lowercased, because `@Maya` and `@maya` are one X account and must not be two
 * conversations.
 */
export const SPOT_PREFIX = "spot:";

export function spotIdFor(handle: string): string {
  return `${SPOT_PREFIX}${handle.replace(/^@/, "").toLowerCase()}`;
}

/** A row that is a registration rather than a knock (WR H9, FR-6.3). */
export function isHeldSpot(c: Conversation): boolean {
  return c.stage === "pre-knock";
}

/** The owner's own thread. One id, because there is only ever one of it. */
export const OWN_ID = "own";

/**
 * The knock being written right now. One id, like `OWN_ID` and for the same
 * reason: a person drafts one knock at a time, and opening a second door to
 * write at replaces the first rather than accumulating a pile of unsent drafts
 * nobody asked to keep.
 *
 * It is a namespace of its own beside the server's thread ids, `held:` and
 * `spot:` so that `knockRefOf` answers `null` for it — the held spot's own
 * safety property (WR H9), and it matters more here: a row that is not yet a
 * knock must not be resolvable to one by the desk, the rail or the transcript
 * reader, because there is no id in the world for them to resolve it to.
 */
export const COMPOSE_ID = "compose";

/** A knock being written, as opposed to one that exists. */
export function isCompose(c: Conversation): boolean {
  return c.stage === "composing";
}

/**
 * The side a row WEARS — the one that opened later, or the only one there is.
 *
 * A thread has at most two, one per direction, and the list draws one face. The
 * tie-break is the moment each conversation opened, which is the order a person
 * met them in. `null` for every row that is not a knock thread.
 */
function pickFace(incoming: Side | null, outgoing: Side | null): Side | null {
  if (!incoming) return outgoing;
  if (!outgoing) return incoming;
  return outgoing.openedAt > incoming.openedAt ? outgoing : incoming;
}

export function faceOf(c: Conversation): Side | null {
  return pickFace(c.incoming, c.outgoing);
}

/** Both sides a row has, in a fixed order. Empty for every row that is not a knock thread. */
export function sidesOf(c: Conversation): Side[] {
  return [c.incoming, c.outgoing].filter((s): s is Side => s !== null);
}

/**
 * Which knock a row is ABOUT right now, for the surfaces that still speak in
 * raw ids: the side it wears. Returns `null` for the owner's own thread, a held
 * spot and a draft, none of which is a knock.
 */
export function knockRefOf(c: Conversation): { side: "in" | "out"; knockId: string } | null {
  const face = faceOf(c);
  if (!face) return null;
  return { side: face.direction === "incoming" ? "in" : "out", knockId: face.knockId };
}

/** What a row says while the doorman has not published a read yet (§6.3). */
export const HEARING_IN_PROGRESS = "Hearing in progress.";

/**
 * AND WHAT IT SAYS WHEN THE OWNER GOT THERE FIRST (b unnamed 1 and 2, 17b).
 *
 * The verbs are offered at every moment (§3), so an owner may open the door,
 * decline or block while the hearing is still running — and opening it SETTLES
 * that hearing where it stands (`knocks/scoring.ts#settleOnLetIn`): no read is
 * ever published for that knock, and `assessed` stays false for the life of the
 * row. The line above was chosen on `!assessed` alone, so every one of those
 * knocks wore "Hearing in progress." for ever, beside a chip reading `Through`.
 * It was not a stale row — a reload said the same thing — it was the wrong
 * sentence, and no amount of re-reading could fix it.
 *
 * Said in the owner's own terms, because what happened is a thing the owner
 * did. The card's longer version is built on this literal (`knock-card.tsx`),
 * for the reason the guard's stop is: one fact, said one way, wherever it is
 * said.
 */
export const DECIDED_UNREAD_PREVIEW = "You decided before the read.";
export const DECIDED_UNREAD_LINE = `${DECIDED_UNREAD_PREVIEW} There is no summary on this knock.`;

/**
 * What a row says when the door is open BOTH WAYS (§9, F-57).
 *
 * THE PAIR'S FACT OUTRANKS EITHER SIDE'S, on the row exactly as it does on the
 * chip and on the desk's Stage line. A connected pair is two knocks, and the
 * newer of them is very often still being heard — so the row wore `Connected`
 * beside the sentence "Hearing in progress.", which reads as one of the two
 * being wrong (gauntlet walk 2026-09-13 round two). Both were true; neither
 * was the thing the row is for.
 *
 * The desk's longer note is built on this literal (`right-rail.tsx`), for the
 * same reason the guard's stop is: one fact, said one way, wherever it is said.
 */
export const CONNECTED_PREVIEW = "You knocked on each other.";

/**
 * What a row says when the GUARD stopped the doorman listening (§8). One
 * sentence, and it names a safety stop rather than a verdict: nobody judged
 * this person, the doorman simply stopped, and the owner may still decide.
 *
 * IT IS THE BARE STOP, and the card's two reasoned variants are built on this
 * literal (`components/inbox/labels.ts`). Defined here because this is the
 * lower layer — the list needs the sentence and must not reach up into a
 * component for it — and defined ONCE because a row and the card it opens
 * saying the stop two ways is the drift this file exists to prevent.
 */
export const GUARD_CLOSED_PREVIEW = "The doorman stopped this hearing.";

/**
 * THE STAGE, FROM THE GATE (§6.3), and it is the same table in both
 * directions. A guard-closed row reads turned away on the sender's side; the
 * recipient's keeps whatever the gate says, because the gate did not move (§8).
 */
function stageOfGate(gate: MyKnock["gate"]): Stage {
  if (gate === "let_in") return "through";
  // `blocked` is turned away as far as any list is concerned: the recipient is
  // never sent the row at all, and the sender is simply outside.
  if (gate === "declined" || gate === "blocked") return "turned-away";
  return "at-door";
}

export function sideFromInboxKnock(knock: RecipientKnock): Side {
  // The badge of §6.3's first row. A declined or let-in knock is past the badge
  // whatever the doorman is still doing, and a guard-closed one has stopped.
  const inFlight = !knock.assessed && knock.gate === "at_door" && !knock.guard_closed;
  return {
    knockId: knock.id,
    direction: "incoming",
    stage: stageOfGate(knock.gate),
    openedAt: atOf(knock.created_at),
    at: atOf(knock.created_at),
    // The band ranks against neighbours; the BAR is absolute. A knock that
    // missed the bar reads needs-attention whatever its band says — at a quiet
    // door a weak knock can sit mid-band among other weak knocks.
    //
    // A DECISION OUTRANKS A BAND (§3): a knock the owner let in reads as a good
    // fit whatever the doorman made of it, and one let in before any assessment
    // has no score at all — `cleared_bar` is true by the gate.
    //
    // A DEMOTION IS READ NEXT, because it is the most recent thing anybody said
    // about this knock and it was said by the owner. The doorman's band and bar
    // are untouched underneath and the payload still carries both; what the
    // face and the row show is the correction.
    //
    // AN UNASSESSED KNOCK HAS NO STANDING YET, and "held" is the neutral face
    // for a knock nobody has banded — the same face a sent knock wears.
    standing:
      knock.gate === "let_in"
        ? "good-fit"
        : !knock.assessed
          ? "held"
          : knock.demoted
            ? "needs-attention"
            : knock.standing.cleared_bar
              ? standingOf(knock.standing.band)
              : "needs-attention",
    // The two contradict, and the server has already resolved it: demoting a
    // knock retracts its Save. This reads what came back rather than deciding
    // again, so the client cannot disagree with the row it was sent.
    saved: knock.saved,
    demoted: knock.demoted,
    archived: knock.archived,
    assessed: knock.assessed,
    inFlight,
    guardClosed: knock.guard_closed,
    notRecommended: knock.nob_call !== null && !knock.nob_call.recommended,
    // The guard's stop is said before the hearing's state, because it is what
    // actually happened to this hearing; a summary that exists is said after,
    // because by then the doorman has spoken. Between them sits the owner's own
    // decision, which outranks the hearing for the same reason the chip's does:
    // a gate that has moved is the last thing that happened at this door, and
    // a hearing behind it is over (`DECIDED_UNREAD_PREVIEW`).
    preview: knock.guard_closed
      ? GUARD_CLOSED_PREVIEW
      : knock.assessed
        ? knock.summary || "No summary on file."
        : knock.gate === "at_door"
          ? HEARING_IN_PROGRESS
          : DECIDED_UNREAD_PREVIEW,
  };
}

export function sideFromMyKnock(knock: MyKnock): Side {
  const inFlight = !knock.assessed && knock.gate === "at_door" && !knock.guard_closed;
  return {
    knockId: knock.id,
    direction: "outgoing",
    // The guard closing the hearing is the door not opening, exactly as being
    // declined is — one stage, two ways to get there, and the preview line
    // below is what tells them apart.
    stage: knock.guard_closed ? "turned-away" : stageOfGate(knock.gate),
    openedAt: atOf(knock.created_at),
    at: atOf(knock.updated_at),
    // Never read for an outgoing row — `moodFor` asks direction first — but a
    // field that exists must hold something true, and "held" is the honest
    // default for a knock nobody has banded.
    standing: "held",
    // Nobody saves their own outgoing knock; Save is a thing done TO a knock at
    // your door. Nor demotes one — and a sender is never told either way.
    saved: false,
    demoted: false,
    // ...but they DO archive one, and the server answers per side: this is the
    // sender's own mark on their own copy (R-0828-1).
    archived: knock.archived,
    assessed: knock.assessed,
    inFlight,
    guardClosed: knock.guard_closed,
    preview: outgoingPreview(knock),
  };
}

/**
 * THE ROW (§6.3): one thread, at most one knock per direction, wearing one of
 * them.
 *
 * Everything the list reads off the row — direction, stage, standing,
 * timestamp, the override marks — is the face's; `archived` is the exception,
 * because putting a thread away puts both of the reader's copies away.
 */
export function fromThread(thread: Thread, unread: number): Conversation {
  const incoming = thread.incoming ? sideFromInboxKnock(thread.incoming) : null;
  const outgoing = thread.outgoing ? sideFromMyKnock(thread.outgoing) : null;
  const sides = [incoming, outgoing].filter((s): s is Side => s !== null);
  // The parser already refuses a thread with neither side; this is the type
  // system being told the same thing.
  const face = pickFace(incoming, outgoing);
  if (!face) throw new Error("a thread with no knock in it is not a conversation");
  return {
    id: thread.id,
    kind: "knock",
    direction: face.direction,
    stage: face.stage,
    name: thread.counterparty.display_name,
    handle: thread.counterparty.handle,
    // The pair's fact first (§9) — see `CONNECTED_PREVIEW`. Everywhere else the
    // face's own line stands, because everywhere else the face is the whole of
    // what the row is about.
    preview: thread.connected ? CONNECTED_PREVIEW : face.preview,
    // THE SERVER'S OWN DERIVATION WHERE THERE IS ONE (§6.3). It is the only
    // party that can say when a DECLINED conversation last moved — the rule is
    // "only when the read moves", and no timestamp on these rows encodes it.
    // Absent, the face's own moment stands, exactly as it did before.
    at: atOf(thread.last_activity_at) || face.at,
    unread,
    standing: face.standing,
    saved: face.saved,
    demoted: face.demoted,
    archived: sides.every((s) => s.archived),
    avatar: thread.counterparty.avatar,
    assessed: face.assessed,
    inFlight: face.inFlight,
    guardClosed: face.guardClosed,
    notRecommended: face.notRecommended === true,
    connected: thread.connected,
    incoming,
    outgoing,
  };
}

/**
 * Where a sent knock got to, in one line.
 *
 * A DECISION OUTRANKS EVERYTHING (§3), because it is the last thing that
 * happened and the only line here that reports a person rather than a process.
 * Under it the guard's stop, then the read, and — at the bottom — the honest
 * "your move", because a knock nobody has read yet is a conversation waiting on
 * the sender. Nothing here ever says the conversation is over: it is not.
 */
export function outgoingPreview(knock: MyKnock): string {
  if (knock.gate === "let_in") return OUTGOING_LET_IN;
  if (knock.gate === "declined" || knock.gate === "blocked") return "They passed on this one.";
  if (knock.guard_closed) return "Refused at the door.";
  /**
   * **A PAUSED HEARING IS STILL AT THE DOOR** (ruling R-0921-2 (5)). Under the
   * decisions and the guard, because none of those have happened: nobody has
   * turned this sender away, and what it is waiting on is their own allowance.
   * Above the read for the same reason — the read has not happened either.
   *
   * NO MONEY WORD IN IT. "Hearing paused" is what occurred; the plan screen is
   * where the allowance is discussed, and the notice at the top of the console
   * is what points at it.
   */
  if (knock.paused) return OUTGOING_PAUSED;
  if (!knock.assessed) return OUTGOING_ACTIVE;
  /**
   * AFTER THE READ, THE FACT AND NOT THE VERDICT (ruling R-0925-1 (1)). This
   * line used to be the band — "A weak knock — too few knocks here yet to
   * rank" — beside a chip reading "On them", which read as a verdict the
   * knocker was still waiting on. What happened is that Nob sent its read to
   * the owner; the band stays on the standing card in the thread, and whether
   * Nob recommended them is said nowhere (R-0908-4: the recipient decides).
   * A held read is the same fact — the owner has it — and the thread's own
   * out-of-room row is what says the knocker's copy is waiting on room.
   */
  return readSentLine(knock.door_display_name);
}

/**
 * "Nob has sent its read to Sam." — the list row, and the first half of the
 * desk's note (`right-rail.tsx`), so the two cannot say it two ways.
 */
export function readSentLine(name: string): string {
  return `Nob has sent its read to ${name.trim() || "them"}.`;
}

/**
 * The same fact with the decision named as the owner's (R-0925-1 (1)) — the
 * knocker's desk says it, and so does the standing card where a door's rules
 * treat a person in danger and there is no ranking line to show.
 */
export function sentNote(name: string): string {
  return `${readSentLine(name)} What happens next is theirs.`;
}

/** The sender's own line for a hearing the wall stopped (ruling R-0921-2 (5)). */
export const OUTGOING_PAUSED = "At the door, hearing paused.";

/** The fields every row that is NOT a knock thread shares: no knock, nothing open both ways. */
const NOT_A_THREAD = {
  assessed: false,
  inFlight: false,
  guardClosed: false,
  connected: false,
  incoming: null,
  outgoing: null,
};

/**
 * A held spot — somebody who registered interest before this door existed.
 *
 * THE ID NAMESPACE IS THE POINT. `held:` is a namespace beside the server's
 * thread ids, so `knockRefOf` returns `null` for it and every surface that
 * resolves a row to a knock — the desk, the rail's standing block, the
 * transcript reader — finds nothing to resolve. A held spot cannot be walked
 * into a knock code path by accident, because the row carries no knock (WR
 * H9, integration guide §4.1).
 *
 * `standing` is `"held"` because the field must hold something and that is the
 * neutral face; nothing reads it, because `moodFor` asks the stage first and
 * FR-G9.3 stamps no standing on a spot that earned none.
 */
export function fromHeldSpot(spot: HeldSpot, unread: number): Conversation {
  const shown = spot.display_name.trim();
  return {
    id: `${HELD_PREFIX}${spot.id}`,
    kind: "knock",
    direction: "incoming",
    stage: "pre-knock",
    name: shown || (spot.handle ? `@${spot.handle}` : "Someone who was waiting"),
    handle: spot.handle,
    preview: spot.line.trim() || "They held a spot before this door opened.",
    spot: { line: spot.line.trim(), stopped: false },
    at: atOf(spot.created_at),
    unread,
    standing: "held",
    // There is no knock to save yet (WR H9) — Save acts on `recipient_actions`,
    // and a held spot has no `knocks` row for one to point at. Nor to demote,
    // nor to archive: `knock_marks` is keyed on a knock id that does not exist.
    saved: false,
    demoted: false,
    archived: false,
    avatar: null,
    ...NOT_A_THREAD,
  };
}

/**
 * The other end of a held spot: a spot the READER holds (owner, 2026-08-27).
 *
 * The same stage as `fromHeldSpot` and the opposite direction, which is the
 * whole of the difference — and the reason `stage` is what every surface reads
 * rather than direction alone. `pre-knock` means nobody has been assessed;
 * `outgoing` means the person who has not been assessed is you.
 *
 * WHAT THE ROW MAY SAY IS BOUNDED BY H2. The handle has no door, so nobody at
 * it has signed up, and there is no display name, no picture and no count of
 * anything to put on this row — the handle they were seen at is the whole of
 * what this product knows. That is why `name` is the handle rather than a
 * lookup: a name here would be a profile of somebody who never agreed to have
 * one.
 *
 * `preview` is THEIR OWN LINE, which is the point of the thread: it is the one
 * sentence they wrote, and the thread exists so they can read it back and fix
 * it. A WITHDRAWN spot's preview is the fact that they stopped instead (owner,
 * F-27): the row stays in the list — stopping released the spot, not the
 * conversation — and a row still wearing the held-spot line would read as a
 * spot still held.
 */
export function fromMyWaiting(spot: MyWaiting, unread: number): Conversation {
  const handle = spot.handle.replace(/^@/, "");
  return {
    id: spotIdFor(handle),
    kind: "knock",
    direction: "outgoing",
    stage: "pre-knock",
    name: `@${handle}`,
    handle,
    preview: spot.state === "withdrawn" ? SPOT_STOPPED_PREVIEW : spot.line.trim() || SPOT_PREVIEW,
    // The line stands whatever became of the spot: stopping released the spot,
    // not the sentence they wrote, and the thread keeps it as history.
    spot: { line: spot.line.trim(), stopped: spot.state === "withdrawn" },
    at: atOf(spot.created_at),
    unread,
    standing: "held",
    // Nothing has been judged, so there is nothing to overrule in either
    // direction — and neither verb acts on a row like this one anyway. Nor is
    // there a knock to archive (WR H9).
    saved: false,
    demoted: false,
    archived: false,
    avatar: null,
    ...NOT_A_THREAD,
  };
}

/** A spot whose line came back empty. Never a blank row. */
export const SPOT_PREVIEW = "You are holding a spot here.";

/** A spot that was let go of. One copy: the row's preview and the thread's note. */
export const SPOT_STOPPED_PREVIEW = "You stopped waiting.";

/** What the row says while there is nothing typed in it yet. */
export const COMPOSE_PREVIEW = "Writing a knock.";

/** A hearing that is open and waiting on the sender. Said in exactly one place. */
export const OUTGOING_ACTIVE = "Your move.";

/**
 * The door opened (§3). The sender IS told now — the gate is on their own view
 * and it carries the recipient's own decision — and this is the one line that
 * says it in the list. What follows is a conversation between two people.
 */
export const OUTGOING_LET_IN = "They opened the door.";

/**
 * The knock being written, as a row (owner, 2026-08-26).
 *
 * A signed-in person knocks from inside the console now, so the draft has to be
 * SOMEWHERE in the list while they write it — otherwise switching to another
 * conversation to check something loses the way back to their own half-written
 * knock. `at` is the moment it was opened, which is a true timestamp and not
 * the `Date.now()` `atOf` refuses: this row really was created just now.
 *
 * `avatar` is null and `standing` is "held" for the same reason every other
 * outgoing row has them — the list draws the DOORMAN on a knock you sent, not
 * the person behind the door, and nobody has banded anything here.
 */
export function composeConversation(input: {
  handle: string;
  name: string;
  draft: string;
  at: number;
  /**
   * The knock has gone in and the server's own row has not come back yet.
   *
   * It is a state that lasts about as long as one round trip, and it has to be
   * drawn or the row disappears out of the list at the exact moment somebody
   * pressed send. Staged `at-door` because that is what it now is — a hearing,
   * open, waiting on the sender — and the server's row replaces this one under
   * the same id the moment it arrives.
   */
  sent: boolean;
}): Conversation {
  return {
    id: COMPOSE_ID,
    kind: "knock",
    direction: "outgoing",
    stage: input.sent ? "at-door" : "composing",
    name: input.name,
    handle: input.handle,
    preview: input.sent ? OUTGOING_ACTIVE : input.draft.trim() || COMPOSE_PREVIEW,
    at: input.at,
    unread: 0,
    standing: "held",
    saved: false,
    demoted: false,
    // Nothing to put away: the server has never heard of this one.
    archived: false,
    avatar: null,
    ...NOT_A_THREAD,
  };
}

/** The owner's own row. `preview` is the doorman's last word, if it has spoken. */
export function ownConversation(input: {
  handle: string;
  name: string;
  lastWord: string;
  at: number;
  unread: number;
}): Conversation {
  return {
    id: OWN_ID,
    kind: "own-nob",
    stage: "at-door",
    name: input.name,
    handle: input.handle,
    preview: input.lastWord,
    at: input.at,
    unread: input.unread,
    standing: "good-fit",
    saved: false,
    demoted: false,
    // The owner's own thread is pinned and is not a conversation with anybody
    // else; there is nothing here to tidy away.
    archived: false,
    avatar: null,
    ...NOT_A_THREAD,
  };
}

/**
 * The counts on the filter chips.
 *
 * `passed` is the one that needed a decision: "through the filter and waiting
 * on you" is, in this product, an inbox knock the owner has not answered yet —
 * the doorman already did its filtering to put it there at all.
 */
export function filterCount(list: Conversation[], id: FilterId): number {
  return list.filter((c) => c.kind === "knock" && matchesFilter(c, id)).length;
}

export function matchesFilter(c: Conversation, id: FilterId): boolean {
  if (c.kind === "own-nob") return false;
  // ARCHIVED IS THE FIRST QUESTION, ABOVE EVERY OTHER PREDICATE (R-0828-1). It
  // is a partition, not a flag: a row the reader put away is in Archived and in
  // nothing else, and every other list — `all` included, which is the archive in
  // the old sense of "everything still in play" — is what is left. Writing it as
  // a clause inside each arm would let one future arm forget it, which is
  // precisely how a tidy-up stops tidying.
  if (c.archived) return id === "archived";
  if (id === "archived") return false;
  if (id === "all") return true;
  if (id === "incoming") return c.direction === "incoming";
  if (id === "outgoing") return c.direction === "outgoing";
  // Active, read literally off FR-6.2: everything you sent (whatever became of
  // it) plus the incoming ones you answered, which is what letting somebody
  // through IS here.
  //
  // NO HELD SPOT IS ACTIVE, IN EITHER DIRECTION (owner, 2026-08-27), and the
  // stage is named here rather than left to fall out. An incoming one falls out
  // by itself — nobody sent it and nobody let it through — but a spot the
  // READER holds is outgoing, and "everything you sent" would have swept it in.
  // Nothing was sent: there is no door there yet, and nobody is waiting on a
  // reply. It belongs in Outgoing, which is where the reader goes to find the
  // handles they are waiting on, and nowhere else.
  if (id === "active") {
    return c.stage !== "pre-knock" && (c.direction === "outgoing" || c.stage === "through");
  }
  // Passed: incoming, still standing there, and EITHER the doorman cleared the
  // bar OR the owner kept it back by hand.
  //
  // Cleared is the standing saying so — a knock the doorman ranked at the
  // bottom stands in Incoming and nowhere else, which is the whole difference
  // between the two chips ("Everyone who knocks lands here, cleared or not" vs
  // "Through the filter"). The old predicate skipped that condition and every
  // sample knock "passed", however bad.
  //
  // The `|| c.saved` is the owner's override, and it is why Save is a verb at
  // all: the bar is the doorman's judgement and the door is the owner's, so a
  // Save puts a knock in the list the owner actually works from even when it
  // missed. It is never silent — `doorChipKind` stamps such a row "Saved —
  // didn't pass" — because an override the owner cannot see is indistinguishable
  // from a filter that does not work. A dismissal takes the row out of here by
  // failing the stage test, not by a fourth clause: "Not for me" moves it to
  // `turned-away`.
  //
  // `pre-knock` fails the stage test too, which is the whole reason held spots
  // are staged.
  //
  // NOTHING THE DOORMAN HAS NOT READ HAS PASSED ANYTHING (§6.3): with no
  // assessment there is no bar cleared and nothing waiting on the owner but
  // patience, so an unassessed knock stands in Incoming, where everything that
  // arrived stands. It is `c.assessed` and not `!c.inFlight`, because a
  // guard-closed knock is neither in flight nor assessed and has cleared
  // nothing either.
  //
  // `!c.demoted` is the last word and it is a CLAUSE OF ITS OWN, deliberately,
  // even though `sideFromInboxKnock` already forces a demoted row to
  // `needs-attention` and the server already retracted its Save. Two of the
  // three inputs to this predicate are things the owner said, and the demotion
  // is the one that says "this should not be in this list" in so many words.
  // Leaving it implicit in `standing` would mean a future Save — or any change
  // to how the standing is coarsened — could quietly put it back.
  return (
    c.direction === "incoming" &&
    c.stage === "at-door" &&
    c.assessed &&
    !c.demoted &&
    (c.standing !== "needs-attention" || c.saved)
  );
}

/**
 * The desk summary (PRD FR-6.4, WR FR-G9.1) — the state of the door in one line.
 *
 * _"4 at your door. 3 still waiting on other nobs. 2 already through. 6 holding
 * a spot from before you opened."_ Four clauses over the same list the sidebar
 * draws, so the sentence and the chips can never disagree; nothing is fetched
 * for it. It counts THREADS — people — which is what "at your door" means.
 *
 * EVERY CLAUSE IS DROPPED WHEN IT IS ZERO, including the first. A brand new door
 * saying "0 at your door. 0 still waiting on other nobs." is a sentence about
 * nothing, and the held-spot clause exists precisely because it is the one
 * number such a door CAN show (FR-6.4). An entirely empty desk returns `null`
 * and the caller renders nothing.
 *
 * A KNOCK STILL BEING WRITTEN IS COUNTED NOWHERE, and it falls out without
 * being named: `composing` is outgoing but is not `at-door`, so the second
 * clause skips it. Nobody is "still waiting on another nob" over a draft.
 *
 * SAVED IS NOT A FIFTH COUNTER, and that is the point. A saved knock is still
 * one person standing at the door, so it is counted once, in `atDoor`, exactly
 * as it would be without the Save — the override changes which LIST the row
 * appears in, never how many people are there. A dismissed knock falls through
 * every clause and is counted nowhere: they are not at the door any more.
 */
export function deskSummary(list: Conversation[]): string | null {
  let atDoor = 0;
  let waiting = 0;
  let through = 0;
  let held = 0;
  for (const c of list) {
    if (c.kind !== "knock") continue;
    // AN ARCHIVED ROW IS COUNTED NOWHERE (R-0828-1). The desk is a sentence
    // about the working lists, and the chips it has to agree with have already
    // dropped this row; a count that included it would be the summary insisting
    // somebody is at the door when the door's owner has filed them away.
    if (c.archived) continue;
    // "Holding a spot from before you opened" is a sentence about THIS DOOR, so
    // only the spots held AT it are counted. A spot the owner holds at somebody
    // else's handle is counted nowhere at all: nobody is at their door over it,
    // and nobody is waiting on another nob either, because there is no nob
    // there to wait on.
    if (c.stage === "pre-knock") {
      if (c.direction === "incoming") held += 1;
    } else if (c.direction === "outgoing") {
      if (c.stage === "at-door") waiting += 1;
    } else if (c.stage === "through") through += 1;
    else if (c.stage === "at-door") atDoor += 1;
  }
  const clauses: string[] = [];
  if (atDoor > 0) clauses.push(`${atDoor} at your door.`);
  if (waiting > 0) clauses.push(`${waiting} still waiting on other nobs.`);
  if (through > 0) clauses.push(`${through} already through.`);
  if (held > 0) clauses.push(`${held} holding a spot from before you opened.`);
  return clauses.length > 0 ? clauses.join(" ") : null;
}

/** Name, handle and preview — the three things on the row that can be read. */
export function matchesSearch(c: Conversation, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return `${c.name} @${c.handle} ${c.preview}`.toLowerCase().includes(q);
}

export function sortConversations(list: Conversation[], sort: SortId): Conversation[] {
  const out = [...list];
  if (sort === "oldest") {
    // A row with no timestamp has nothing to be oldest ABOUT, so it stays at
    // the bottom in both directions rather than jumping to the top here.
    out.sort((a, b) => (a.at || Infinity) - (b.at || Infinity));
    return out;
  }
  if (sort === "unread") {
    out.sort((a, b) => (b.unread > 0 ? 1 : 0) - (a.unread > 0 ? 1 : 0) || b.at - a.at);
    return out;
  }
  out.sort((a, b) => b.at - a.at);
  return out;
}

/** The list the sidebar draws, filtered, searched and sorted, own row excluded. */
export function visibleConversations(input: {
  conversations: Conversation[];
  filter: FilterId;
  sort: SortId;
  search: string;
}): Conversation[] {
  const kept = input.conversations.filter(
    (c) => matchesFilter(c, input.filter) && matchesSearch(c, input.search),
  );
  return sortConversations(kept, input.sort);
}
